<template>
  <label for="name" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Select Content Type</label>
  <Multiselect
    v-if="!contentTypesLoading && !contentLoading"
    v-model="selectedContentType"
    class="my-4"
    :allow-empty="false"
    :searchable="true"
    placeholder="Select Content Type"
    :options="contentTypeOptions"
    :object="true"
    label="name"
    value-prop="id" />

  <div v-if="contentTypesLoading || contentLoading" class="my-4" role="status">
    <UiSpinner :size="4" class="mr-3" />
  </div>

  <div v-if="selectedContentType && Object.keys(selectedContentType.dynamicInputs || {}).length > 0" class="mt-6">
    <div class="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <div class="mb-4 text-sm font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
        Dynamic Inputs
      </div>
      <div v-for="[inputName, inputType] in selectedContentType.dynamicInputs" :key="inputName">
        <UiFormControl
          :label="inputName"
          :required="true"
          class="!mb-4">
          <UiFormInput
            v-if="inputType === 'single'"
            v-model="dynamicInputValues[inputName]"
            :placeholder="inputName"
            size="sm" />
          <UiFormTextarea
            v-if="inputType === 'list'"
            v-model="dynamicInputValues[inputName]"
            :placeholder="inputName"
            size="sm" />
          <div v-if="getLocationHint(inputName)" class="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Default: {{ getLocationHint(inputName) }}
          </div>
        </UiFormControl>
      </div>
    </div>
  </div>

  <div v-if="selectedContentType" class="mt-6">
    <a
      v-if="!contentLoading"
      href="/"
      class="btn btn-success"
      @click.prevent="createContent()">
      Generate Article
    </a>

    <button
      v-if="contentLoading"
      disabled
      type="button"
      class="btn btn-default">
      <UiSpinner class="mr-3" />
      Loading...
    </button>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, defineProps } from 'vue'
import { createToast } from 'mosha-vue-toastify'
import Multiselect from '@vueform/multiselect'
import { UiFormControl } from '@ui/form/control'
import { UiFormInput } from '@ui/form/input'
import { UiFormTextarea } from '@ui/form/textarea'
import UiSpinner from '@ui/spinner.vue'
import { getContentTypes } from '@/api/users/content_types'
import { getLocationForUser } from '@/api/users/locations'
import { generateContentEntity } from '@/api/users/content_entities'

const props = defineProps({
  locationId: String,
  contentTypeId: String
})

const locationData = ref(null)

const contentTypeOptions = ref([])
const selectedContentType = ref(null)
const contentTypesLoading = ref(true)
const dynamicInputValues = ref({})

const getContentTypesData = () => {
  getContentTypes().then((response) => {
    contentTypesLoading.value = false
    contentTypeOptions.value = response.data
    selectedContentType.value = contentTypeOptions.value.find((contentType) => contentType.id === props.contentTypeId)
  }).catch((error) => console.log(error))
}

const contentLoading = ref(false)

watch(() => props.locationId, async (newLocationId) => {
  if (newLocationId) {
    try {
      const response = await getLocationForUser({ id: newLocationId })
      locationData.value = response.data
    } catch (error) {
      console.error('Failed to load location data:', error)
      locationData.value = null
    }
  } else {
    locationData.value = null
  }
}, { immediate: true })

// Get location hint for dynamic input
const getLocationHint = (inputName) => {
  if (!locationData.value) return null

  const value = locationData.value[inputName]
  return value && typeof value === 'string' ? value : null
}

const createContent = () => {
  contentLoading.value = true
  createToast('Content creating... you will be redirected on completion.', { type: 'success' })
  const payload = {
    contentTypeId: selectedContentType.value.id,
    locationId: props.locationId,
    dynamicInputs: dynamicInputValues.value
  }

  generateContentEntity(payload)
    .then((response) => {
      window.location = `/users/ai_content/content_entities/${response.data.id}`
    })
    .catch((error) => {
      createToast(error.response?.data?.error || 'Unexpected error occurred.', { type: 'danger' })
      console.log(error)
    })
    .finally(() => {
      contentLoading.value = false
    })
}

onMounted(getContentTypesData)
</script>
